/**
 * DispatchTable - 调度数据表格组件
 * 
 * 这是一个功能完整的数据表格组件，专门用于显示和管理容器调度任务。
 * 
 * 主要功能：
 * - 显示容器任务列表数据
 * - 支持多字段排序功能
 * - 支持多选和全选操作
 * - 集成批量操作按钮
 * - 响应式表格设计
 * 
 * 设计特点：
 * - 表头可点击排序，带有视觉反馈
 * - 支持复杂的表格行展开功能
 * - 优化的移动端显示（隐藏次要列）
 * - 集成操作按钮和状态指示
 * 
 * @author Claude Code Assistant
 * @version 1.0
 */

import React, { useState, useCallback, useEffect } from 'react'

import { Card, CardContent } from '../ui/card';
import { ArrowUp, ArrowDown, Package, X } from 'lucide-react';
import { TaskTableRow } from './TaskTableRow';
import { TaskDetailModal } from './TaskDetailModal';
import { PaginationControls } from './PaginationControls';
import {
  ContainerTask,
  SortField,
  StatusConfig,
  TaskTypeConfig
} from '../../types/container';
import { PaginationResult } from '../../hooks/usePagination';
import { onHoldSectionOf, type OnHoldSection } from '../../hooks/useTaskData';

/**
 * On Hold 三段的表头样式。
 *
 * 结构和配色都跟派单页 Container List 的日期隔断（TODAY / THURSDAY, SEPTEMBER 10）
 * 保持一致：通栏淡色带、圆点 + 大写加粗标签 + 一条细线拉到底，标的是它下面那一段。
 *
 * 颜色按「能不能马上动手」分：绿=付掉就能提，琥珀=只能等海关，青=其余（和日期行同色）。
 */
const SECTION_HEADER: Record<OnHoldSection, { label: string; band: string; dot: string; text: string; rule: string }> = {
  'gate-fee': {
    label: 'Awaiting Gate Fee · Pay to Release',
    band: 'bg-emerald-100 border-emerald-300',
    dot: 'bg-emerald-500',
    text: 'text-emerald-900',
    rule: 'bg-emerald-300',
  },
  'closed-area': {
    label: 'Closed Area · Container Cannot Be Pulled',
    band: 'bg-rose-100 border-rose-300',
    dot: 'bg-rose-500',
    text: 'text-rose-900',
    rule: 'bg-rose-300',
  },
  exam: {
    label: 'Transfer for Exam',
    band: 'bg-amber-100 border-amber-300',
    dot: 'bg-amber-500',
    text: 'text-amber-900',
    rule: 'bg-amber-300',
  },
  other: {
    label: 'Other On Hold',
    band: 'bg-[#00838F]/[0.14] border-[#00838F]/40',
    dot: 'bg-[#00838F]',
    text: 'text-[#005E6B]',
    rule: 'bg-[#00838F]/35',
  },
}

// 组件属性接口定义
interface DispatchTableProps {
  sortedData: ContainerTask[]  // Current page data
  allSortedData: ContainerTask[]  // All sorted data for selection logic
  /**
   * 当前这份数据是不是按「查验 / 普通扣柜」分过组了。
   *
   * 必须由调用方明说，不能靠 DispatchTable 自己猜。分组只在筛了 On Hold 时才
   * 做（见 groupCustomsExamFirst），而「数据里同时存在两类」在任何视图下都可能
   * 成立 —— 只看后者的话，没筛 On Hold 的混合列表顶上也会冒出一个
   * 「OTHER ON HOLD」的标题，指着一堆 Loading / Yard Stored 的行。
   */
  examGroupingActive?: boolean
  paginationResult: PaginationResult<ContainerTask>
  selectedTasks: string[]
  expandedRows: Set<string>
  markedTaskIds: Set<string>
  sortField: SortField | null
  sortDirection: "asc" | "desc" | null
  statusConfig: Record<string, StatusConfig>
  taskTypeConfig: Record<string, TaskTypeConfig>
  drivers: string[]
  handleSelectAll: (checked: boolean) => void
  handleSelectTask: (taskId: string, checked: boolean) => void
  toggleRowExpansion: (taskId: string) => void
  toggleMarked: (taskId: string) => void
  handleSort: (field: SortField) => void
  formatDateTime: (dateString: string) => string
  getLFDUrgency: (lfdDate: string) => string
  getLFDStyles: (urgency: string) => string
  handleDispatchTask: () => void
  onEditContainerDetails: (task: ContainerTask) => void
  onUpdateContainer: (updatedContainer: ContainerTask) => void
  /** Re-pull the list after the detail modal wrote to the server on its own (reroute). */
  onServerUpdate?: () => void
  autoOpenTaskId?: string | null
  onAutoOpenHandled?: () => void
  /** B3 Fix Plate — passed straight through to each row's Fix Plate button. */
  onFixPlate?: (appointmentId: number) => void
  /** Gate Fee — passed straight through to each row's Gate Fee button. */
  onGateFee?: (taskId: number | string) => void
  /** Batch Book —— 直接透传给每一行的批量预约按钮。 */
  onBatchBook?: (taskId: number | string) => void
  selectedOrderingParty?: string
  onOrderingPartyClick?: (name: string) => void
}

export const DispatchTable: React.FC<DispatchTableProps> = ({
  sortedData,
  allSortedData,
  examGroupingActive = false,
  paginationResult,
  markedTaskIds,
  sortField,
  sortDirection,
  statusConfig,
  taskTypeConfig,
  toggleMarked,
  handleSort,
  formatDateTime,
  getLFDUrgency,
  getLFDStyles,
  onUpdateContainer,
  onServerUpdate,
  autoOpenTaskId,
  onAutoOpenHandled,
  onFixPlate,
  onGateFee,
  onBatchBook,
  selectedOrderingParty,
  onOrderingPartyClick,
}) => {
  // Single shared modal state — lifted from individual TaskTableRow instances
  const [modalTask, setModalTask] = useState<ContainerTask | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const handleOpenDetail = useCallback((task: ContainerTask) => {
    setModalTask(task)
    setModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  // Auto-open task detail modal when autoOpenTaskId is set (e.g., from ?taskId= URL param)
  useEffect(() => {
    if (!autoOpenTaskId || allSortedData.length === 0) return
    const task = allSortedData.find(t => t.id === autoOpenTaskId || t.taskId === autoOpenTaskId)
    if (task) {
      setModalTask(task)
      setModalOpen(true)
      onAutoOpenHandled?.()
    }
  }, [autoOpenTaskId, allSortedData, onAutoOpenHandled])

  // 每一段的柜子数，取全量而不是本页 —— 分段和分页是两回事，交界点可能落在
  // 第二页，那时本页交界点上面的行数只是这一段的尾巴。
  const sectionTotals = allSortedData.reduce((acc, t) => {
    const k = onHoldSectionOf(t)
    acc[k] = (acc[k] || 0) + 1
    return acc
  }, {} as Record<OnHoldSection, number>)
  // 画组标题要两个条件同时成立：调用方确实分了段，且真的分出了不止一段。
  // 少了前一个，没筛 On Hold 的列表顶上也会冒出组标题；少了后一个，只有一段
  // 的时候会挂一个什么也没分开的标题。
  const groupingActive =
    examGroupingActive && Object.values(sectionTotals).filter((n) => n > 0).length > 1

  return (
    <Card className="flex-1 min-w-0 rounded-lg shadow-sm border border-gray-100 flex flex-col min-h-0 overflow-hidden">
      <CardContent className="p-0 flex flex-col flex-1 min-h-0">
        <div className="overflow-y-auto overflow-x-hidden flex-1">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gradient-to-b from-slate-50 to-slate-100/95 border-b border-slate-200 text-[11px] font-semibold tracking-wider text-slate-500 uppercase backdrop-blur-sm">
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[140px]">
                  <button
                    onClick={() => handleSort("taskId")}
                    className="flex items-center hover:text-[#00838F] transition-colors"
                  >
                    Task ID
                    {sortField === "taskId" && (
                      <span className="ml-1">
                        {sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[180px]">
                  Container Info
                </th>
                {/* 120 而不是 100：Loading / Empty 的行在状态下面还挂一条
                    「Out 9/10 · 0d」的出闸日标签，100 装不下会把别的列挤窄 */}
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[130px]">
                  <button
                    onClick={() => handleSort("status")}
                    className="flex items-center hover:text-[#00838F] transition-colors"
                  >
                    Status
                    {sortField === "status" && (
                      <span className="ml-1">
                        {sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[120px] hidden md:table-cell">
                  <div className="flex items-center gap-1">
                    <span>Ord. Party</span>
                    {selectedOrderingParty && selectedOrderingParty !== 'all' && (
                      <button
                        onClick={() => onOrderingPartyClick?.(selectedOrderingParty)}
                        title={`Clear filter: ${selectedOrderingParty}`}
                        className="text-[#00838F] hover:text-[#006064] transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[80px]">
                  <button
                    onClick={() => handleSort("lfd")}
                    className="flex items-center hover:text-[#00838F] transition-colors"
                  >
                    LFD
                    {sortField === "lfd" && (
                      <span className="ml-1">
                        {sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[110px] hidden lg:table-cell">
                  <button
                    onClick={() => handleSort("taskType")}
                    className="flex items-center hover:text-[#00838F] transition-colors"
                  >
                    Task Type
                    {sortField === "taskType" && (
                      <span className="ml-1">
                        {sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[100px]">
                  <button
                    onClick={() => handleSort("driver")}
                    className="flex items-center hover:text-[#00838F] transition-colors"
                  >
                    Driver
                    {sortField === "driver" && (
                      <span className="ml-1">
                        {sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500 w-[260px] hidden md:table-cell">
                  Route
                </th>
                <th className="text-right px-3 py-2 font-semibold text-slate-500 w-[60px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, index) => {
                // 总数取自全量列表而不是本页：交界点可能落在第二页，那时本页
                // 上面的行数只是这一组的尾巴，报出去就是个错数字。
                const isEvenRow = index % 2 === 1
                const isMarked = markedTaskIds.has(item.id)
                // 组标题：和派单页 Container List 的日期隔断同一套样式和结构 ——
                // 一条通栏的淡色带，圆点 + 大写标签 + 一条细线拉到底，标的是它
                // 下面那一组。
                //
                // 每组各一条，而不是中间只放一条：分页会把交界点冲到某一页中间，
                // 只有一条的话，翻到下一页就只剩下半组而没有任何说明；而且"中间
                // 一条"到底在说上面还是下面，本身就有歧义。
                const section = onHoldSectionOf(item)
                const needsHeader =
                  groupingActive &&
                  (index === 0 || onHoldSectionOf(sortedData[index - 1]) !== section)
                const hdr = SECTION_HEADER[section]

                return (
                  <React.Fragment key={`grp-${item.id}`}>
                  {needsHeader && (
                    <tr>
                      <td colSpan={10} className={`px-3 py-1.5 border-y ${hdr.band}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${hdr.dot}`} aria-hidden="true"></span>
                          <span className={`text-xs font-bold uppercase tracking-wider ${hdr.text}`}>
                            {hdr.label}
                          </span>
                          <span className={`flex-1 h-px ${hdr.rule}`} aria-hidden="true"></span>
                        </div>
                      </td>
                    </tr>
                  )}
                  <TaskTableRow
                    key={item.id}
                    task={item}
                    isEvenRow={isEvenRow}
                    isMarked={isMarked}
                    toggleMarked={toggleMarked}
                    statusConfig={statusConfig}
                    taskTypeConfig={taskTypeConfig}
                    formatDateTime={formatDateTime}
                    getLFDUrgency={getLFDUrgency}
                    getLFDStyles={getLFDStyles}
                    onUpdateContainer={onUpdateContainer}
                    onOpenDetail={handleOpenDetail}
                    onFixPlate={onFixPlate}
                    onGateFee={onGateFee}
                    onBatchBook={onBatchBook}
                    selectedOrderingParty={selectedOrderingParty}
                    onOrderingPartyClick={onOrderingPartyClick}
                  />
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>

          {/* Empty / sparse data hint */}
          {sortedData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Package className="w-10 h-10 mb-3 text-slate-300" />
              <p className="text-sm font-medium">No containers found</p>
              <p className="text-xs mt-1">Try adjusting your filters or add a new container</p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        <PaginationControls
          currentPage={paginationResult.currentPage}
          totalPages={paginationResult.totalPages}
          totalItems={paginationResult.totalItems}
          itemsPerPage={paginationResult.itemsPerPage}
          startIndex={paginationResult.startIndex}
          endIndex={paginationResult.endIndex}
          hasNextPage={paginationResult.hasNextPage}
          hasPreviousPage={paginationResult.hasPreviousPage}
          goToPage={paginationResult.goToPage}
          goToNextPage={paginationResult.goToNextPage}
          goToPreviousPage={paginationResult.goToPreviousPage}
          setItemsPerPage={paginationResult.setItemsPerPage}
        />
      </CardContent>

      {/* Single shared Task Detail Modal — rendered once instead of per-row */}
      {modalTask && (
        <TaskDetailModal
          task={modalTask}
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={onUpdateContainer}
          onServerUpdate={onServerUpdate}
          statusConfig={statusConfig}
          allContainerTasks={allSortedData}
        />
      )}
    </Card>
  )
}